import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import type { Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseLernfoerderung, isCourseOfActiveGeneration } from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { map } from 'rxjs';
import type { KPF } from '../../../../src/public-access/api/KPFPublicInformationAPI.js';
import { BoardRoutes } from '../routes.js';
import './board-component-course-startdate.js';
import './board-component-flat-loader.js';

/**
 * @customElement board-component-lkk-overview-grid
 */
@customElement('board-component-lkk-overview-grid')
export class BoardComponentLkkOverviewGrid extends ChemistryLitElement {
  @property({ attribute: false }) public kpf: KPF | undefined = undefined;
  @property({ attribute: false }) public courses = new RXController(this, [] as Course[]);
  @property({ attribute: false }) public loading = false;
  @property({ attribute: false }) public hide: string[] = [];
  @property() public name = '';
  @property() public class = '';

  @property({ type: Boolean }) public noToggle = false;
  @property({ type: Boolean }) public noHeader = false;

  // Is set if location time or day is missing
  @property({ attribute: false }) public hasMissingData = false;

  @state() private readonly _courseMap = new RXController(
    this,
    this.courses.observable.pipe(
      map(allCourses => {
        const courseMap = new Map<string, Course[]>();
        const courses = allCourses.filter(c => isCourseOfActiveGeneration(c, this.kpf?.produkt));

        if (this._isLKKV2()) {
          courseMap.set('V2', courses);
          return courseMap;
        }

        for (const course of courses) {
          if (!this.hasMissingData)
            this.hasMissingData = !course.startTime || !course.endTime || !course.location || !course.weekday;

          const entry = courseMap.get(course.location);
          if (!entry) {
            courseMap.set(course.location, [course]);
          } else {
            courseMap.set(course.location, [...entry, course]);
          }
        }
        return courseMap;
      }),
    ),
  );

  private _isLKKV2() {
    return this.kpf?.produkt instanceof ProductLKK && this.kpf?.produkt.useV2Courses;
  }

  override render() {
    const content = this.loading || !this.kpf ? this._loader() : this._content();

    if (this.noToggle) {
      return content;
    }

    return html`
      <x-toggle-content
        .label=${html`
          <cr-h4> ${this.name} </cr-h4> ${this.hasMissingData
            ? html`
                <cr-icon ${css({ color: this.colors.tone.error, fontSize: '1.8rem' })}>
                  report_problem
                  <cr-tooltip> <cr-text> In dieser ${this.name} fehlen Daten! </cr-text> </cr-tooltip>
                </cr-icon>
              `
            : nothing}
        `}
      >
        ${content}
      </x-toggle-content>
    `;
  }

  private _loader() {
    return html` <cr-flex w100 center crossaxis-center> <cr-loader></cr-loader> </cr-flex> `;
  }

  private _columns() {
    if (this._isLKKV2()) {
      return '100px 120px 1fr 1fr';
    }
    return '150px 100px 120px 1fr 1fr';
  }

  private _header() {
    return html`
      ${this.noHeader
        ? nothing
        : html`<cr-grid columns="${this._columns()}" space="md">
            ${this._isLKKV2() ? nothing : html`<cr-text bold>Raum</cr-text>`}
            <cr-text bold>Tag</cr-text>
            <cr-text bold>Zeit</cr-text>
            ${!this.hide.includes('Jugendlich') ? html`<cr-text bold>Jugendliche</cr-text>` : html`<cr-text></cr-text>`}
            ${!this.hide.includes('Kinder') ? html`<cr-text bold>Kinder</cr-text>` : html`<cr-text></cr-text>`}
          </cr-grid>`}
    `;
  }

  private _content() {
    return html`
      <cr-flex horizontal space="md" space-between crossaxis-center ${css({ overflow: 'auto' })}>
        <cr-flex space="md" center>
          ${this._header()}
          ${this.courses.value.length
            ? html`
                ${repeat(
                  this._courseMap.value?.keys() ?? [],
                  l => l,
                  (location, locationIndex) => this._location(location, locationIndex),
                )}
              `
            : this.class === CourseLernfoerderung._class
              ? html`
                  <cr-flex center crossaxis-center horizontal space="sm">
                    <cr-text> Es sind keine Lernförderungskurse für dieses KPF eingetragen. </cr-text>
                  </cr-flex>
                `
              : nothing}
        </cr-flex>

        ${this.class
          ? html`
              <cr-flex center crossaxis center>
                <cr-button
                  mode="fab"
                  icon="chevron_right"
                  href=${`${BoardRoutes.details}/${this.class}/${this.kpf?._id}`}
                >
                  <cr-tooltip> <cr-text> Details </cr-text> </cr-tooltip>
                </cr-button>
              </cr-flex>
            `
          : nothing}
      </cr-flex>
    `;
  }

  private _locationCell(location: string) {
    if (this._isLKKV2()) {
      return nothing;
    }

    return html`
      <cr-flex center crossaxis center ${css({ gridRow: `1 / span ${this._courseMap.value?.get(location)?.length}` })}>
        ${location !== ''
          ? location.startsWith('http')
            ? html`<cr-link href=${location}
                ><cr-flex horizontal crossaxis-center
                  ><cr-icon ${css({ marginRight: '10px' })}>computer</cr-icon>
                  <cr-text>digitaler Kurs</cr-text></cr-flex
                ></cr-link
              >`
            : html` <cr-text bold> ${location} </cr-text> `
          : html`
              <cr-flex horizontal space="xs">
                <cr-text bold ${css({ color: this.colors.tone.error })}> Raum </cr-text>
                <cr-text bold ${css({ color: this.colors.tone.error, fontSize: '1.4rem' })}> ? </cr-text>
              </cr-flex>
            `}
      </cr-flex>
    `;
  }

  private _location(location: string, locationIndex: number) {
    const firstCourse = this._courseMap.value?.get(location)?.at(0);
    return html`
      <cr-flex space="md">
        ${(firstCourse?.plannedStartDate ?? firstCourse?.startDate)
          ? html`<board-component-course-startdate
              .plannedStartdate=${firstCourse.plannedStartDate}
              .actualStartdate=${firstCourse.startDate}
            ></board-component-course-startdate>`
          : nothing}
        <cr-grid columns="${this._columns()}" space="md">
          ${this._locationCell(location)}
          ${repeat(
            this._courseMap.value?.get(location) ?? [],
            course => course._id,
            course => this._course(course),
          )}
        </cr-grid>
      </cr-flex>
      ${locationIndex + 1 === Array.from(this._courseMap.value?.keys() ?? []).length
        ? nothing
        : html` <cr-flex w100 ${css({ height: '1px', background: this.colors.tone.subtle })}></cr-flex> `}
    `;
  }
  private _course(course: Course) {
    const produkt = this.kpf?.produkt;
    if (!(produkt instanceof ProductLKK)) throw new Error('only use with Lernkaskade Produkt');
    const osStudentsPercentage = this._getPercentage(course.osStudentIDs.length, produkt.OSLernFoerderung ?? 3);
    const usStudentsPercentage = this._getPercentage(course.usStudentIDs.length, produkt.USLernFoerderung ?? 12);

    return html`
      <cr-flex center>
        ${course.weekday
          ? html` <cr-text> ${course.weekday} </cr-text> `
          : html`
              <cr-flex horizontal space="xs">
                <cr-text ${css({ color: this.colors.tone.error })}> Tag </cr-text>
                <cr-text bold ${css({ color: this.colors.tone.error, fontSize: '1.4rem' })}> ? </cr-text>
              </cr-flex>
            `}
      </cr-flex>
      <cr-flex center>
        ${course.startTime && course.endTime
          ? html` <cr-text> ${course.startTime.toFormat('HH:mm')} - ${course.endTime.toFormat('HH:mm')} </cr-text> `
          : html`
              <cr-flex horizontal space="xs">
                <cr-text ${css({ color: this.colors.tone.error })}> Uhrzeit </cr-text>
                <cr-text bold ${css({ color: this.colors.tone.error, fontSize: '1.4rem' })}> ? </cr-text>
              </cr-flex>
            `}
      </cr-flex>

      ${this._percentageBar(
        !this.hide.includes('Jugendlich'),
        osStudentsPercentage,
        course.osStudentIDs.length,
        produkt.OSLernFoerderung,
        false,
        this._isLKKV2(),
      )}
      ${this._percentageBar(
        !this.hide.includes('Kinder'),
        usStudentsPercentage,
        course.usStudentIDs.length,
        produkt.USLernFoerderung,
        false,
        this._isLKKV2(),
      )}
    `;
  }

  private _percentageBar(
    visibile: boolean,
    value: number,
    count: number,
    all: number | undefined | null,
    isSum = false,
    hideAll = false,
  ) {
    if (!visibile) {
      return html`<cr-text></cr-text>`;
    }

    const countText = count.toString();
    const allText = hideAll ? '' : `/ ${all ?? '?'}`;
    const summaryText = isSum ? 'insg.' : '';
    const displayText = [countText, allText, summaryText].filter(Boolean).join(' ');

    return html`<cr-flex ${css({ alignItems: 'flex-start' })}>
      <cr-flex space="sm">
        <cr-progress-bar
          ${css({ maxWidth: '180px' })}
          .value=${value}
          tone=${this._getProgressBarTone(value)}
        ></cr-progress-bar>
        <cr-text>${displayText}</cr-text>
      </cr-flex>
    </cr-flex>`;
  }

  private _getPercentage(actualNumber: number, neededNumber: number) {
    if (!actualNumber || !neededNumber || actualNumber < 0 || neededNumber < 0) return 0;
    return actualNumber / neededNumber;
  }

  private _getProgressBarTone(percentage: number) {
    if (percentage <= 0.2) return 'error';
    if (percentage < 1.0) return 'warning';
    return 'success';
  }
}
